/**
 * Rich text <-> Zalo styles conversion utilities.
 *
 * Zalo styles format (intermediate, translated server-side for zca-js):
 *   { start: number, len: number, st: 'b'|'i'|'u'|'s'|'c'|'sz', v?: string }
 *
 * st codes:
 *   'b'  = bold
 *   'i'  = italic
 *   'u'  = underline
 *   's'  = strikethrough
 *   'c'  = color        (v = hex string, e.g. "#ff0000")
 *   'sz' = size         (v = 'small' | 'normal' | 'large' | 'xlarge')
 */

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "LI",
  "UL",
  "OL",
  "BLOCKQUOTE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);

const FONT_SIZE_MAP = {
  1: "small",
  2: "small",
  3: "normal",
  4: "large",
  5: "large",
  6: "xlarge",
  7: "xlarge",
};

function rgbToHex(rgb) {
  if (!rgb) return null;
  if (rgb.startsWith("#")) return rgb.toLowerCase();
  const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return null;
  const toHex = (n) => Number(n).toString(16).padStart(2, "0");
  return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`;
}

function pxToSizeName(px) {
  const n = parseFloat(px);
  if (!Number.isFinite(n)) return null;
  if (n <= 12) return "small";
  if (n <= 15) return "normal";
  if (n <= 20) return "large";
  return "xlarge";
}

function extractStyleCodes(el) {
  const tag = el.tagName?.toUpperCase();
  const codes = [];
  if (tag === "B" || tag === "STRONG") codes.push({ st: "b" });
  if (tag === "I" || tag === "EM") codes.push({ st: "i" });
  if (tag === "U") codes.push({ st: "u" });
  if (tag === "S" || tag === "STRIKE" || tag === "DEL") codes.push({ st: "s" });

  const style = el.style;
  if (style) {
    if (
      style.fontWeight &&
      (style.fontWeight === "bold" || parseInt(style.fontWeight, 10) >= 600)
    ) {
      if (!codes.some((c) => c.st === "b")) codes.push({ st: "b" });
    }
    if (style.fontStyle === "italic") {
      if (!codes.some((c) => c.st === "i")) codes.push({ st: "i" });
    }
    if (style.textDecorationLine?.includes("underline") || style.textDecoration?.includes("underline")) {
      if (!codes.some((c) => c.st === "u")) codes.push({ st: "u" });
    }
    if (style.textDecorationLine?.includes("line-through") || style.textDecoration?.includes("line-through")) {
      if (!codes.some((c) => c.st === "s")) codes.push({ st: "s" });
    }
    if (style.color) {
      const hex = rgbToHex(style.color);
      if (hex) codes.push({ st: "c", v: hex });
    }
    if (style.fontSize) {
      const size = pxToSizeName(style.fontSize);
      if (size) codes.push({ st: "sz", v: size });
    }
  }

  if (tag === "FONT") {
    const color = el.getAttribute("color");
    if (color) {
      const hex = rgbToHex(color);
      if (hex) codes.push({ st: "c", v: hex });
    }
    const size = el.getAttribute("size");
    if (size && FONT_SIZE_MAP[size]) {
      codes.push({ st: "sz", v: FONT_SIZE_MAP[size] });
    }
  }

  return codes;
}

function mergeStyles(styles) {
  const bySig = new Map();
  for (const s of styles) {
    const sig = s.v ? `${s.st}:${s.v}` : s.st;
    if (!bySig.has(sig)) bySig.set(sig, []);
    bySig.get(sig).push(s);
  }
  const merged = [];
  for (const runs of bySig.values()) {
    runs.sort((a, b) => a.start - b.start);
    let cur = null;
    for (const r of runs) {
      if (cur && r.start <= cur.start + cur.len) {
        cur.len = Math.max(cur.len, r.start + r.len - cur.start);
      } else {
        if (cur) merged.push(cur);
        cur = { ...r };
      }
    }
    if (cur) merged.push(cur);
  }
  return merged.sort((a, b) => a.start - b.start);
}

export function htmlToZaloStyles(html) {
  if (!html || typeof document === "undefined") {
    return { text: html || "", styles: [], mentions: [] };
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  let text = "";
  const styles = [];

  const walk = (node) => {
    if (node.nodeType === 3 /* TEXT_NODE */) {
      text += node.textContent;
      return;
    }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return;

    const tag = node.tagName.toUpperCase();

    if (tag === "BR") {
      text += "\n";
      return;
    }

    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock && text.length > 0 && !text.endsWith("\n")) {
      text += "\n";
    }

    if (tag === "LI") {
      const parent = node.parentElement;
      if (parent?.tagName === "OL") {
        const idx = Array.from(parent.children).indexOf(node) + 1;
        text += `${idx}. `;
      } else {
        text += "• ";
      }
    }

    const codes = extractStyleCodes(node);
    const starts = codes.map((c) => ({ ...c, start: text.length }));

    for (const child of node.childNodes) walk(child);

    for (const s of starts) {
      const len = text.length - s.start;
      if (len > 0) {
        styles.push({ start: s.start, len, st: s.st, ...(s.v ? { v: s.v } : {}) });
      }
    }
  };

  for (const child of container.childNodes) walk(child);

  return {
    text: text.replace(/\n{3,}/g, "\n\n").trim(),
    styles: mergeStyles(styles),
    mentions: [],
  };
}

export function stripHtml(html) {
  return htmlToZaloStyles(html).text;
}

export function plainToHtml(text) {
  if (!text) return "";
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .split("\n")
    .map((line) => `<p>${line.length ? line : "<br>"}</p>`)
    .join("");
}

export function hasContent(html) {
  if (!html) return false;
  return stripHtml(html).trim().length > 0;
}
